
/*eslint-disable  @typescript-eslint/no-unused-vars*/

import React, { FC,  useCallback,  useMemo, useRef, useState } from 'react';
import {  CustomButtonGroup,  DashboardLoader, useAppDispatch, useAppSelector } from '../../../globalUtils/globalExports';
import { checkValidAlphaNumericAndHyphen, createBreadCrumbProps, isInputWithSpace, keyPressValidationAlphaNumericAndHyphen, useEffectOnce  } from '../../../globalUtils/globalHooks';
import CustomForm, { ICustomFormProps, IFormContentProp } from '../../../globalUtils/CustomForm/CustomForm';
import { cloneDeep } from 'lodash';
import { getAcReferenceDataReducer, generateAcChargerCertificates, setAcChargerCertificatesInfoListTableProps, getAcCertificatesReducer , fetchAcChargerProductTypeReferenceData } from '../../rmsReduxStore/acReduxExports';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';


interface AcChargerCertificateData{
    visibleId: string | null;
    macId: string | null;
    productTypeId: IGeneralReferenceData | null;
}

const CreateAcChargerCertificate: FC = () => {
    
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const initialAcChargerCertificateData: AcChargerCertificateData = {
        visibleId: null,
        macId: null,
        productTypeId: null
    }
    const {chargerProductTypeReferenceData} = useAppSelector(getAcReferenceDataReducer);
    const {acChargerCertificatesInfoListTableProps} = useAppSelector(getAcCertificatesReducer);
    const [acChargerCertificateFormData, setAcChargerCertificateFormData] = useState<AcChargerCertificateData>(initialAcChargerCertificateData);
    const acChargerCertificateFormDataRef = useRef<AcChargerCertificateData>(acChargerCertificateFormData);
    const [showLoader, setShowLoader] = useState(false);


    useEffectOnce(()=>{
      dispatch(fetchAcChargerProductTypeReferenceData())
    })

    const keyPressValidationForMACAddress = useCallback((event): void => {
        const keyPressed = event.key;
        // Allow Ctrl+C (copy) and Ctrl+V (paste) and Backspace
        if (keyPressed === 'Backspace' || ((event.ctrlKey || event.metaKey) && (keyPressed.toLowerCase() === 'c' || keyPressed.toLowerCase() === 'v'))) return;
        // Check Only HEx and allow - and : 
        if (!(/^[0-9a-fA-F\-:]+$/.test(keyPressed))) event.preventDefault();
    },[])

    const checkValidMAC = useCallback((value: string): string => {
        // Regular expression to validate a MAC address
        return /^[0-9a-fA-F\-:]+$/.test(value) ? value : ''
    },[])


    const handleFormDataChange = useCallback((val, formKey) => {
        const formData = cloneDeep(acChargerCertificateFormDataRef?.current);
        
        formData[formKey] = val;
        acChargerCertificateFormDataRef.current = formData;
        setAcChargerCertificateFormData(formData);
    }, []);

    const handleSubmit = async () => {
        setShowLoader(true);
        const reqBody = {
            visibleId: acChargerCertificateFormData.visibleId,
            macId: acChargerCertificateFormData.macId,
            productTypeId: acChargerCertificateFormData.productTypeId?.id
        }

        const  response = await dispatch(generateAcChargerCertificates(reqBody));

        setShowLoader(false);

        if ((response?.status === 200  || response?.status === 201 || response?.status === 202)) {
            setShowLoader(false)
            toast.success(response?.data?.message);
            const tableProps = cloneDeep(acChargerCertificatesInfoListTableProps);
            tableProps.sortBy = 'createdAt';
            tableProps.order = 'descending';
            await dispatch(setAcChargerCertificatesInfoListTableProps(tableProps));
            navigate('/ac/charger-certificates')
        } else {
            setShowLoader(false)
            toast.error(response?.data?.message)
        }
    }
  const formArray: IFormContentProp[] = useMemo(()=>[
    {
      rowCountClassName: 'grid-3',
      formObject: [
        {
          formLabel: 'Charger Serial Number',
          isRequired: true,
          objectType: 'input',
          selectDropdownProps: null,
          inputProps: {
            inputPlaceHolder:'Enter Serial Number',
            handleInputChange: (event): void => {
                handleFormDataChange(checkValidAlphaNumericAndHyphen(isInputWithSpace(event?.target?.value)), 'visibleId');
            },
            inputValue: acChargerCertificateFormData?.visibleId ?? '',
            inputType: 'string',
            inputFieldId: 'charger_visible_id',
            isRequired: true,
            isDisabled:false,
            autoFocus: true,
            handleKeyPress:keyPressValidationAlphaNumericAndHyphen
          },
          textAreaProps: null,
        },
        {
            formLabel: 'MAC Address',
            isRequired: true,
            objectType: 'input',
            selectDropdownProps: null,
            inputProps: {
              inputPlaceHolder:'Enter MAC Address',
              handleInputChange: (event): void => {
                handleFormDataChange(checkValidMAC(event?.target?.value), 'macId');
              },
              inputValue: acChargerCertificateFormData?.macId ?? '',
              inputType: 'string',
              inputFieldId: 'charger_mac_id',
              isRequired: true,
              isDisabled:false,
              autoFocus: false,
              handleKeyPress: keyPressValidationForMACAddress,
            },
            textAreaProps: null,
          },
          {
            formLabel: 'Product Type',
            isRequired: true,
            objectType: 'dropdown',
            selectDropdownProps: {
                selectValue: acChargerCertificateFormData?.productTypeId,
                selectOptions: chargerProductTypeReferenceData ?? [],
                handleSelectOptionChange: (val): void => {
                    handleFormDataChange(val,'productTypeId');
                },
                selectDropDownId: 'charger-product-type-id',
                selectDropDownName: 'charger_product_type_id',
            },
            inputProps: null,
            textAreaProps: null,
          },
      ],
    }
  ],[acChargerCertificateFormData, chargerProductTypeReferenceData]);
 
  const isSubmitBtnDisabled = useMemo(() => {
    return !(acChargerCertificateFormData.visibleId && acChargerCertificateFormData.macId && acChargerCertificateFormData.productTypeId?.id );
  },[acChargerCertificateFormData]);

  const btnsList = useMemo(() => {
    return [
      {
        buttonText: 'Generate',
        buttonId: 'add',
        btnClassName: isSubmitBtnDisabled ? 'primary__btn disabled' : 'primary__btn',
        handleClick: handleSubmit, 
        isDisabled: isSubmitBtnDisabled,
        buttonVariant: 'filled',
      },
      {
        buttonText: 'Cancel',
        buttonId: 'cancel',
        btnClassName: 'secondary__btn',
        handleClick: (): void => {
            //Fix: cancel button not working
            navigate('/ac/charger-certificates')
        },
        isDisabled: false,
        buttonVariant: 'outlined',
      },
    ];
  }, [isSubmitBtnDisabled,navigate,handleSubmit]);

  const breadCrumbs = createBreadCrumbProps({
    breadCrumbProps:
      [
        {
          objectType: 'link',
          id: 'chargerCertificates',
          link: '/ac/charger-certificates'
        },
        {
          objectType: 'text',
          id: 'text',
          text: 'Generate Certificate'
        }
      ]
  })
  const customFormProps: ICustomFormProps = useMemo(() => {
    return {
      formContainerClassName: 'add__parameter__wrap',
      breadCrumbprops: {
        containerId: 'add-parameter-breadcrumb',
        containerClassName: 'add-parameter-breadcrumb',
        breadCrumbs: breadCrumbs
      },
      headerPropTypes: {
        header: 'Generate Certificate',
        headerClassName: 'add__parameter__wrap-header-text',
      },
      formArray: formArray
    };
  }, [formArray])
    return (

     <>
      <DashboardLoader showLoader={showLoader} />
      <div className='charger__form__wrap'>
        <div className='charger__form__wrap-content'>
          <CustomForm {...customFormProps} />
        </div>
        <div className='charger__form__wrap-footer'>
          <CustomButtonGroup
            buttonsList={btnsList}
            buttonGroupClassName='button__group__footer' />
        </div>
      </div>
    </>
  );
};
export default CreateAcChargerCertificate;

